// The confirm panel (P2.3 guard, spec §5): every readiness check commerce
// reports, in its fixed order, with pass / fail. Confirm stays disabled until
// commerce says canConfirm; with no active terms at all (code no_active_terms)
// the reason is exactly "Waiting for Fern's cancellation and no-show terms"
// (ruling F). Availability "Unknown, can't confirm" never looks like "Busy".
// Presentation only — commerce re-checks everything inside one transaction.
import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, CircleSlash, HelpCircle, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { DataState } from '@/features/shared/components/ui/DataState';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import { getApiErrorMessage } from '@/lib/apiError';
import {
  type Booking, type ConfirmReadiness, type ReadinessCheck, type ReadinessSlot, confirmBooking, getConfirmReadiness,
} from '@/services/baBookingService';
import { formatInZone } from './bookingFormat';
import { type K, useRunAction } from './shared';

export const NO_ACTIVE_TERMS_REASON = 'Waiting for Fern’s cancellation and no-show terms';

const CHECK_LABEL: Record<string, string> = {
  confirm_enabled: 'Confirmation is enabled for this company',
  booking_state: 'The booking is awaiting confirmation',
  terms: 'Cancellation and no-show terms are active',
  quote: 'The client’s acceptance of the current quote is recorded',
  funding: 'Funding evidence is recorded',
  slots: 'Every slot has an accepted invitation',
  availability: 'Every ambassador is available',
};

export const checkLabel = (c: ReadinessCheck, k: K) =>
  k(`confirm.check_${c.key}`, CHECK_LABEL[c.key] ?? c.key.replace(/_/g, ' '));

/** Why Confirm is disabled: the terms wait when there are no active terms at
 *  all, else the first failing check's own message. null = can confirm. */
export function disabledReason(r: ConfirmReadiness, k: K): string | null {
  if (r.canConfirm && r.checks.every((c) => c.ok)) return null;
  if (r.checks.some((c) => c.key === 'terms' && !c.ok && c.code === 'no_active_terms')) {
    return k('confirm.no_active_terms', NO_ACTIVE_TERMS_REASON);
  }
  const first = r.checks.find((c) => !c.ok);
  if (!first) return k('confirm.not_ready', 'This booking can’t be confirmed yet.');
  return first.message || checkLabel(first, k);
}

/** Readiness slots and bookable candidates both carry availability + conflict. */
export const AvailabilityBadge: React.FC<{ slot: Pick<ReadinessSlot, 'availability' | 'conflict'>; tz: string; k: K }> = ({ slot, tz, k }) => {
  switch (slot.availability) {
    case 'free':
      return <Badge variant="success" data-availability="free">{k('availability.free', 'Free')}</Badge>;
    case 'busy':
      return (
        <Badge variant="danger" data-availability="busy">
          <XCircle className="w-3 h-3 mr-1" />
          {slot.conflict
            ? k('availability.busy_window', 'Busy {{start}} – {{end}}', {
              start: formatInZone(slot.conflict.start, tz), end: formatInZone(slot.conflict.end, tz),
            })
            : k('availability.busy', 'Busy')}
        </Badge>
      );
    case 'unknown':
      // Deliberately not the busy look: a dashed outline and a question mark —
      // we could not read leave or assignments, so we don't know.
      return (
        <Badge variant="warning" className="border border-dashed border-warning-strong" data-availability="unknown">
          <HelpCircle className="w-3 h-3 mr-1" />{k('availability.unknown', 'Unknown, can’t confirm')}
        </Badge>
      );
    default:
      return <Badge variant="neutral" data-availability="unstaffed">{k('availability.unstaffed', 'No ambassador yet')}</Badge>;
  }
};

export const ConfirmPanel: React.FC<{ booking: Booking; k: K; onConfirmed: (b: Booking) => void; reloadKey?: string | number }> = ({
  booking, k, onConfirmed, reloadKey,
}) => {
  const confirm = useConfirm();
  const { busy, run } = useRunAction(k);
  const [state, setState] = useState<{ s: 'loading' } | { s: 'ok'; r: ConfirmReadiness } | { s: 'error'; msg: string }>({ s: 'loading' });

  const load = useCallback(() => {
    setState({ s: 'loading' });
    getConfirmReadiness(booking.id)
      .then((r) => setState({ s: 'ok', r }))
      .catch((e) => setState({ s: 'error', msg: getApiErrorMessage(e, k('errors.readiness', 'Could not check whether this booking can be confirmed.')) }));
  }, [booking.id, k]);
  useEffect(load, [load, reloadKey]);

  const doConfirm = async (r: ConfirmReadiness) => {
    const ok = await confirm({
      title: k('confirm.title', 'Confirm booking'),
      message: k('confirm.question', 'Confirm “{{title}}”? The ambassadors’ holds become confirmed reservations.', { title: booking.eventTitle }),
      confirmLabel: k('confirm.button', 'Confirm booking'),
    });
    if (!ok) return;
    const out = await run((key) => confirmBooking(booking.id, { version: r.bookingVersion || booking.version, key }),
      k('confirm.done', 'Booking confirmed.'));
    if (out) onConfirmed(out);
    else load(); // something changed: show the checks as they are now
  };

  return (
    <Card data-testid="confirm-panel">
      <CardHeader><CardTitle>{k('confirm.panel_title', 'Ready to confirm?')}</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {state.s === 'loading' && <DataState variant="loading" compact message={k('confirm.checking', 'Checking leave, assignments and the booking… this can take a few seconds.')} />}
        {state.s === 'error' && <DataState variant="error" message={state.msg} onRetry={load} />}
        {state.s === 'ok' && (() => {
          const r = state.r;
          const reason = disabledReason(r, k);
          return (
            <>
              <ul className="space-y-2" aria-label={k('confirm.checks', 'Readiness checks')}>
                {r.checks.map((c) => (
                  <li key={c.key} className="flex items-start gap-2 text-sm" data-check={c.key} data-ok={c.ok ? 'pass' : 'fail'}>
                    {c.ok
                      ? <CheckCircle2 className="w-4 h-4 mt-0.5 text-success-strong shrink-0" aria-hidden />
                      : <CircleSlash className="w-4 h-4 mt-0.5 text-danger-strong shrink-0" aria-hidden />}
                    <div>
                      <p className="text-foreground">
                        {checkLabel(c, k)}{' '}
                        <span className={c.ok ? 'text-success-strong' : 'text-danger-strong'}>
                          {c.ok ? k('confirm.pass', 'Pass') : k('confirm.fail', 'Not yet')}
                        </span>
                      </p>
                      {!c.ok && (
                        <p className="text-xs text-muted-foreground">
                          {c.key === 'terms' && c.code === 'no_active_terms'
                            ? k('confirm.no_active_terms', NO_ACTIVE_TERMS_REASON)
                            : c.message}
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>

              {r.slots.length > 0 && (
                <div className="space-y-1">
                  <h4 className="text-sm font-semibold text-foreground">{k('confirm.slots_title', 'Availability')}</h4>
                  <ul className="space-y-1">
                    {r.slots.map((s) => (
                      <li key={s.slotId} className="flex flex-wrap items-center gap-2 text-sm" data-slot={s.slotId}>
                        <span className="text-foreground">{s.role}</span>
                        <AvailabilityBadge slot={s} tz={booking.timezone} k={k} />
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-3 pt-1">
                <Button disabled={busy || !!reason} onClick={() => doConfirm(r)} data-testid="confirm-booking">
                  {k('confirm.button', 'Confirm booking')}
                </Button>
                <Button variant="outline" size="sm" disabled={busy} onClick={load}>{k('confirm.recheck', 'Check again')}</Button>
              </div>
              {reason && <p className="text-sm text-muted-foreground" data-testid="confirm-reason">{reason}</p>}
            </>
          );
        })()}
      </CardContent>
    </Card>
  );
};

export default ConfirmPanel;
